import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  useChatNotifications,
  getNotificationPath,
  formatNotificationTime,
  type ChatNotification,
  type ChatNotificationType,
} from '@/api/hooks/useChatNotifications';
import { usePushTokenSync } from '@/hooks/usePushTokenSync';
import {
  useNotifications,
  useCombinedUnreadCount,
  useMarkAsRead,
  type Notification,
} from '@/api/hooks/useNotifications';
import { resolveSystemNotificationPath } from '@/lib/notification-links';
import { useWarehouseStore } from '@/stores/warehouse-store';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Spinner } from '@/components/ui/spinner';
import {
  Bell,
  At,
  UserPlus,
  ArrowBendUpLeft,
  Check,
  Hash,
  Package,
  ShoppingCart,
  Warning,
  Info,
} from '@phosphor-icons/react';

// ============================================================================
// Constants
// ============================================================================

const BELL_TAB_STORAGE_KEY = 'notification-bell-tab';
const DEFAULT_TAB = 'all';

// ============================================================================
// Types
// ============================================================================

interface NotificationBellProps {
  className?: string;
}

/** Discriminated union for the merged "All" feed */
type FeedItem =
  | { kind: 'chat'; data: ChatNotification; createdAt: string }
  | { kind: 'system'; data: Notification; createdAt: string };

// ============================================================================
// Helper Functions
// ============================================================================

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getChatIcon(type: ChatNotificationType): React.ReactNode {
  switch (type) {
    case 'MENTION':
      return <At className="h-4 w-4 text-primary" />;
    case 'CHANNEL_INVITE':
      return <UserPlus className="h-4 w-4 text-success" />;
    case 'MESSAGE_REPLY':
      return <ArrowBendUpLeft className="h-4 w-4 text-info" />;
    default:
      return <Bell className="h-4 w-4" />;
  }
}

function getSystemIcon(type: string): React.ReactNode {
  switch (type) {
    case 'LOW_STOCK':
      return <Warning className="h-4 w-4 text-warning" />;
    case 'APPROVAL_REQUIRED':
    case 'OVERDUE_TASK':
      return <Warning className="h-4 w-4 text-destructive" />;
    default:
      return <Info className="h-4 w-4 text-muted-foreground" />;
  }
}

function getChatContextIcon(notification: ChatNotification): React.ReactNode {
  if (notification.channelId) return <Hash className="h-3 w-3" />;
  if (notification.purchaseOrderId) return <Package className="h-3 w-3" />;
  if (notification.salesOrderId) return <ShoppingCart className="h-3 w-3" />;
  return null;
}

function getChatContextLabel(notification: ChatNotification): string {
  if (notification.channel) return `#${notification.channel.name}`;
  if (notification.purchaseOrder) return notification.purchaseOrder.poNumber;
  if (notification.salesOrder) return notification.salesOrder.orderNumber;
  return '';
}

// ============================================================================
// Sub-components
// ============================================================================

interface ChatNotificationItemProps {
  notification: ChatNotification;
  onClick: () => void;
}

function ChatNotificationItem({
  notification,
  onClick,
}: ChatNotificationItemProps) {
  const contextIcon = getChatContextIcon(notification);
  const contextLabel = getChatContextLabel(notification);

  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-accent',
        !notification.read && 'bg-accent/50',
      )}
    >
      {notification.actor ? (
        <Avatar className="h-8 w-8 shrink-0">
          <AvatarFallback className="text-xs">
            {getInitials(notification.actor.name)}
          </AvatarFallback>
        </Avatar>
      ) : (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
          {getChatIcon(notification.type)}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">
            {notification.title}
          </span>
          {!notification.read && (
            <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
          )}
          <Badge
            variant="outline"
            className="ml-auto shrink-0 text-[10px] py-0"
          >
            Chat
          </Badge>
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {notification.body}
        </p>
        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          {contextIcon && (
            <>
              <span className="flex items-center gap-1">
                {contextIcon}
                {contextLabel}
              </span>
              <span>-</span>
            </>
          )}
          <span>{formatNotificationTime(notification.createdAt)}</span>
        </div>
      </div>
    </button>
  );
}

interface SystemNotificationItemProps {
  notification: Notification;
  onClick: () => void;
}

function SystemNotificationItem({
  notification,
  onClick,
}: SystemNotificationItemProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-accent',
        !notification.isRead && 'bg-accent/50',
      )}
    >
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
        {getSystemIcon(notification.type)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">
            {notification.title}
          </span>
          {!notification.isRead && (
            <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
          )}
          <Badge
            variant="outline"
            className="ml-auto shrink-0 text-[10px] py-0"
          >
            System
          </Badge>
        </div>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">
          {notification.message}
        </p>
        <div className="mt-1 text-xs text-muted-foreground">
          {formatNotificationTime(notification.createdAt)}
        </div>
      </div>
    </button>
  );
}

interface ChatNotificationGroupProps {
  title: string;
  notifications: ChatNotification[];
  onNotificationClick: (notification: ChatNotification) => void;
}

function ChatNotificationGroup({
  title,
  notifications,
  onNotificationClick,
}: ChatNotificationGroupProps) {
  if (notifications.length === 0) return null;

  return (
    <div>
      <h4 className="px-4 py-2 text-xs font-medium uppercase text-muted-foreground">
        {title}
      </h4>
      {notifications.map((notification) => (
        <ChatNotificationItem
          key={notification.id}
          notification={notification}
          onClick={() => onNotificationClick(notification)}
        />
      ))}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function NotificationBell({ className }: NotificationBellProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      return localStorage.getItem(BELL_TAB_STORAGE_KEY) || DEFAULT_TAB;
    } catch {
      return DEFAULT_TAB;
    }
  });

  const { selectedWarehouseId, isAllMode } = useWarehouseStore();
  const warehouseId = isAllMode
    ? undefined
    : (selectedWarehouseId ?? undefined);

  // Combined unread count for the badge (single API call)
  const { data: combinedCount } = useCombinedUnreadCount(warehouseId);
  const totalUnread = combinedCount?.total ?? 0;
  const markSystemAsRead = useMarkAsRead();

  // Keep this browser's FCM token registered against its LIVE push
  // subscription (rotations otherwise leave a stale token that FCM 200s to
  // and silently drops).
  usePushTokenSync();

  // Chat notifications (for "Chat" tab and "All" tab merge)
  const {
    notifications: chatNotifications,
    markAsRead: markChatAsRead,
    markAllAsRead: markChatAllAsRead,
    isLoading: chatLoading,
  } = useChatNotifications();

  // System notifications (for "All" tab merge)
  const { data: systemData, isLoading: systemLoading } = useNotifications({
    warehouseId,
    take: 20,
  });
  const systemNotifications: Notification[] = systemData?.data ?? [];

  const isLoading = chatLoading || systemLoading;

  // Persist tab selection
  useEffect(() => {
    try {
      localStorage.setItem(BELL_TAB_STORAGE_KEY, activeTab);
    } catch {
      // localStorage may be unavailable in some environments
    }
  }, [activeTab]);

  // Merge + sort for "All" tab
  const allFeedItems: FeedItem[] = React.useMemo(() => {
    const chatItems: FeedItem[] = chatNotifications.map((n) => ({
      kind: 'chat' as const,
      data: n,
      createdAt: n.createdAt,
    }));
    const systemItems: FeedItem[] = systemNotifications.map((n) => ({
      kind: 'system' as const,
      data: n,
      createdAt: n.createdAt,
    }));
    return [...chatItems, ...systemItems].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [chatNotifications, systemNotifications]);

  // Chat tab groupings
  const mentionNotifications = chatNotifications.filter(
    (n) => n.type === 'MENTION',
  );
  const inviteNotifications = chatNotifications.filter(
    (n) => n.type === 'CHANNEL_INVITE',
  );
  const replyNotifications = chatNotifications.filter(
    (n) => n.type === 'MESSAGE_REPLY',
  );

  const handleChatClick = useCallback(
    async (notification: ChatNotification) => {
      if (!notification.read) {
        await markChatAsRead([notification.id]);
      }
      navigate(getNotificationPath(notification));
      setIsOpen(false);
    },
    [markChatAsRead, navigate],
  );

  const handleSystemClick = useCallback(
    (notification: Notification) => {
      if (!notification.isRead) {
        markSystemAsRead.mutate([notification.id]);
      }
      navigate(
        resolveSystemNotificationPath(
          notification.entityType,
          notification.entityId,
        ),
      );
      setIsOpen(false);
    },
    [navigate, markSystemAsRead],
  );

  const handleMarkAllAsRead = useCallback(async () => {
    await markChatAllAsRead();
  }, [markChatAllAsRead]);

  const hasChatUnread = (combinedCount?.chat ?? 0) > 0;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('relative', className)}
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {totalUnread > 0 && (
            <Badge
              variant="destructive"
              className="absolute -right-1 -top-1 h-5 min-w-[20px] justify-center px-1.5 text-xs"
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-96 p-0" sideOffset={8}>
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold">Notifications</h3>
          {hasChatUnread && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="h-8 gap-1.5 text-xs"
            >
              <Check className="h-3 w-3" />
              Mark chat read
            </Button>
          )}
        </div>

        {/* Tabbed content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full rounded-none border-b bg-transparent px-4 py-0 h-10">
            <TabsTrigger
              value="all"
              className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
            >
              All
              {totalUnread > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-xs">
                  {totalUnread > 99 ? '99+' : totalUnread}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="chat"
              className="flex-1 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
            >
              Chat
              {(combinedCount?.chat ?? 0) > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-xs">
                  {combinedCount!.chat > 99 ? '99+' : combinedCount!.chat}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* All tab: merged chat + system sorted by createdAt desc */}
          <TabsContent value="all" className="mt-0">
            <ScrollArea className="max-h-[360px]">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="lg" />
                </div>
              ) : allFeedItems.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Bell className="mx-auto h-8 w-8 text-muted-foreground" />
                  <p className="mt-2 text-sm text-muted-foreground">
                    No notifications yet
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {allFeedItems.map((item) =>
                    item.kind === 'chat' ? (
                      <ChatNotificationItem
                        key={`chat-${item.data.id}`}
                        notification={item.data}
                        onClick={() => handleChatClick(item.data)}
                      />
                    ) : (
                      <SystemNotificationItem
                        key={`system-${item.data.id}`}
                        notification={item.data}
                        onClick={() => handleSystemClick(item.data)}
                      />
                    ),
                  )}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Chat tab: preserves original 3-section grouping */}
          <TabsContent value="chat" className="mt-0">
            <ScrollArea className="max-h-[360px]">
              {chatLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="lg" />
                </div>
              ) : chatNotifications.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Bell className="mx-auto h-8 w-8 text-muted-foreground" />
                  <p className="mt-2 text-sm text-muted-foreground">
                    No chat notifications yet
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  <ChatNotificationGroup
                    title="Mentions"
                    notifications={mentionNotifications}
                    onNotificationClick={handleChatClick}
                  />
                  {mentionNotifications.length > 0 &&
                    (inviteNotifications.length > 0 ||
                      replyNotifications.length > 0) && <Separator />}
                  <ChatNotificationGroup
                    title="Channel Invites"
                    notifications={inviteNotifications}
                    onNotificationClick={handleChatClick}
                  />
                  {inviteNotifications.length > 0 &&
                    replyNotifications.length > 0 && <Separator />}
                  <ChatNotificationGroup
                    title="Replies"
                    notifications={replyNotifications}
                    onNotificationClick={handleChatClick}
                  />
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {/* Footer: 2-button row */}
        <Separator />
        <div className="flex gap-1 p-2">
          <Button
            variant="ghost"
            className="flex-1 justify-center text-sm"
            onClick={() => {
              navigate('/notifications');
              setIsOpen(false);
            }}
          >
            All notifications
          </Button>
          <Button
            variant="ghost"
            className="flex-1 justify-center text-sm"
            onClick={() => {
              navigate('/chat');
              setIsOpen(false);
            }}
          >
            Open chat
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
